/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { debounce } from 'lodash';

import Imgix from 'js/components/Imgix';

import { Button, Grid, Typography2, breakpoints } from '@coursera/cds-core';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings as CarouselSettingsType } from 'bundles/design-system/components/types';
import TrackedButton from 'bundles/page/components/TrackedButton';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';

import _t from 'i18n!nls/xdp';

const styles = {
  button: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    width: 100%;
    overflow: hidden;
    display: block;
    padding: 0;
  `,
  image: css`
    width: 100%;
    height: auto;

    /* needed to override Imgix's style definition */
    max-width: none !important;
    max-height: none !important;
  `,
  viewAllAction: css`
    padding: 0;
    margin: var(--cds-spacing-200) 0 0 0;

    :hover,
    :focus {
      background: none;
    }
  `,
  carousel: (isMobile: boolean) => css`
    margin: 0 auto;
    padding: var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-600);
    height: ${isMobile ? 'unset' : 'calc(80vh - 80px)'};

    div {
      height: 100%;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-100) var(--cds-spacing-400) var(--cds-spacing-400);
    }
  `,
  carouselImage: (isMobile: boolean, dimension?: { width: string; height: string }) => css`
    width: ${dimension?.width ? dimension.width : 'auto'};
    height: ${dimension?.height ? dimension.height : 'auto'};

    /* needed to override Imgix's style definition */
    display: block !important;
    margin: 0 auto;

    /* center image vertically */
    ${isMobile === false &&
    `
      position: relative;
      top: 50%;
      transform: translateY(-50%);
    `}
  `,
};

type Props = {
  imageUrls?: string[] | null;
};

const CarouselSettings: CarouselSettingsType = {
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: [],
  lazyLoad: 'ondemand',
  isFluid: true,
  dots: true,
  fade: true,
};

const ProjectImageGallery = ({ imageUrls }: Props) => {
  const isMobile = useIsMobile();
  const { isModalOpen, setModalOpen } = useModal();
  const [carouselImageDimension, setCarouselImageDimension] = useState<{ width: string; height: string } | undefined>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const carouselRef = useRef<HTMLInputElement | null>(null);
  const carouselImageRef = useRef<HTMLImageElement | null>(null);

  const updateCarouselImageDimension = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (imageElement: HTMLImageElement) => {
      if (!carouselRef?.current) {
        return;
      }

      if (imageElement.clientWidth > carouselRef?.current?.clientWidth) {
        setCarouselImageDimension({
          width: '100% !important',
          height: 'auto',
        });
      } else if (imageElement.clientHeight > carouselRef?.current?.clientHeight) {
        setCarouselImageDimension({
          width: 'auto !important',
          height: '100%',
        });
      }
    },
    []
  );

  const onResizeWindow = debounce(() => {
    if (carouselImageRef?.current) {
      updateCarouselImageDimension(carouselImageRef?.current);
    }
  }, 100);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('resize', onResizeWindow);
    }

    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('resize', onResizeWindow);
      }
    };
  }, [onResizeWindow]);

  const handleImageOnLoad = (event: React.ChangeEvent<HTMLImageElement>) => {
    updateCarouselImageDimension(event.target);
  };

  const openImageGallery = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (index: number) => () => {
      setModalOpen();
      setCurrentSlideIndex(index);
    },
    [setModalOpen, setCurrentSlideIndex]
  );

  if (!imageUrls?.length) {
    return null;
  }

  const imagesToShow = isMobile ? imageUrls.slice(0, 4) : imageUrls.slice(0, 8);
  const showViewAllAction = imagesToShow.length < imageUrls.length;

  return (
    <Subsection
      title={
        <Typography2 variant="subtitleMedium" component="h4">
          <FormattedMessage
            message={_t('{numberOfImages, plural, =1 {# project image} other {# project images}}')}
            numberOfImages={imageUrls.length}
          />
        </Typography2>
      }
      spacing={32}
    >
      <Grid container spacing={24} component="ul">
        {imagesToShow.map((image, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Grid item xs={6} sm={3} key={index} component="li">
              <TrackedButton
                trackingName="image_gallery_expand"
                onClick={openImageGallery(index)}
                aria-label={_t('Click to expand item')}
                css={styles.button}
              >
                <Imgix
                  src={image}
                  alt=""
                  css={styles.image}
                  maxWidth={200}
                  maxHeight={150}
                  imgParams={{ fit: 'crop' }}
                />
              </TrackedButton>
            </Grid>
          );
        })}
      </Grid>
      {showViewAllAction && (
        <Button variant="ghost" edgeAlign="start" size="small" css={styles.viewAllAction} onClick={openImageGallery(0)}>
          {_t('View all project images')}
        </Button>
      )}
      <Modal
        open={isModalOpen}
        setOpen={setModalOpen}
        width="large"
        hideActions
        keepMounted={false}
        renderInPlace={false}
      >
        <div css={styles.carousel(isMobile)} ref={carouselRef}>
          <Carousel
            settings={{
              ...CarouselSettings,
              prevArrowLabel: _t('Go to previous image'),
              nextArrowLabel: _t('Go to next image'),
              initialSlide: currentSlideIndex,
            }}
          >
            {imageUrls.map((image, index) => (
              // Use the full-sized image for the expanded view.
              <img
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                src={image}
                alt=""
                css={styles.carouselImage(isMobile, carouselImageDimension)}
                onLoad={handleImageOnLoad}
                ref={carouselImageRef}
              />
            ))}
          </Carousel>
        </div>
      </Modal>
    </Subsection>
  );
};

export default ProjectImageGallery;
